import { useEffect, useLayoutEffect, useState } from 'react'
import { locate, plain, rows } from '../../core/tree-read'
import type { Overlay } from '../view-api'
import { useViewportInset } from '@w/utils-fe'
import { blockElement, type OverlayApi } from './shared'

/** Keyboard occlusion belongs to the editing host, never persisted in the document. */
export const VisualViewport: Overlay = ({ api }) => {
  const [active, setActive] = useState(false)
  useEffect(() => {
    const root = api.root
    if (!root) return () => {}
    const activate = () => { setActive(true) }
    root.addEventListener('focusin', activate)
    if (root.contains(root.ownerDocument.activeElement)) activate()
    return () => { root.removeEventListener('focusin', activate) }
  }, [api.root])
  return active ? <ActiveVisualViewport api={api} /> : null
}

function ActiveVisualViewport({ api }: { api: OverlayApi }) {
  const { keyboardOpen } = useViewportInset()
  useEffect(() => {
    const root = api.root
    const view = root?.ownerDocument.defaultView
    if (!root || !view || api.mode !== 'edit' || root.closest('[data-review-mobile-panel]')) return () => {}
    const padding = root.style.paddingBottom
    let base: number | undefined
    const reveal = () => {
      const viewport = view.visualViewport
      const covered = keyboardOpen && viewport ? Math.max(0, view.innerHeight - viewport.height - viewport.offsetTop) : 0
      const toolbarHeight = api.layout === 'touch' && keyboardOpen && root.contains(root.ownerDocument.activeElement) ? 52 : 0
      if (!viewport || !covered && !toolbarHeight) {
        root.style.setProperty('padding-bottom', padding)
        base = undefined
        return
      }
      base ??= Number(view.getComputedStyle(root).paddingBottom.replace(/px$/u, '')) || 0
      root.style.setProperty('padding-bottom', `${base + covered + toolbarHeight}px`)
      const selection = view.getSelection()
      if (!selection?.isCollapsed || !selection.rangeCount || !root.contains(selection.focusNode)) return
      const node = selection.focusNode
      const child = node instanceof Element ? node.childNodes[selection.focusOffset] : null
      const rect = child instanceof Element ? child.getBoundingClientRect() : selection.getRangeAt(0).getBoundingClientRect()
      const bottom = viewport.offsetTop + viewport.height - toolbarHeight
      if (rect.bottom > bottom) view.scrollBy(0, rect.bottom - bottom)
      else if (rect.top < viewport.offsetTop) view.scrollBy(0, rect.top - viewport.offsetTop)
    }
    view.addEventListener('resize', reveal)
    view.visualViewport?.addEventListener('resize', reveal)
    view.visualViewport?.addEventListener('scroll', reveal)
    root.ownerDocument.addEventListener('selectionchange', reveal)
    root.addEventListener('focusin', reveal)
    if (root.contains(root.ownerDocument.activeElement)) reveal()
    return () => {
      view.removeEventListener('resize', reveal)
      view.visualViewport?.removeEventListener('resize', reveal)
      view.visualViewport?.removeEventListener('scroll', reveal)
      root.ownerDocument.removeEventListener('selectionchange', reveal)
      root.removeEventListener('focusin', reveal)
      root.style.setProperty('padding-bottom', padding)
    }
  }, [api.root, api.mode, api.layout, keyboardOpen])
  return null
}

export const Placeholder: Overlay = ({ api }: { api: OverlayApi }) => {
  useLayoutEffect(() => {
    const root = api.root
    if (!root || api.mode !== 'edit') return () => {}
    let active: HTMLElement | null = null
    const clear = () => { if (active) delete active.dataset.cbPlaceholder; active = null }
    const refresh = () => {
      clear()
      if (!root.contains(root.ownerDocument.activeElement)) return
      const selection = api.state.selection
      if (!selection || selection.nodes?.length || selection.table || selection.field ||
        selection.anchor.blockId !== selection.focus.blockId || selection.anchor.offset !== selection.focus.offset) return
      const entries = rows(api.state.blocks)
      const row = entries[locate(entries, selection.focus).row]
      if (!row || !['p', 'td'].includes(row.block.type) || plain(row.block) || row.block.children.some(child => child.type === 'cloze')) return
      if (api.state.blocks.length === 1 && api.state.blocks[0] === row.block) return
      const element = blockElement(api, row.block.id)
      if (!element || element.closest('[data-question-field]')) return
      active = row.block.type === 'td' ? element.querySelector<HTMLElement>('p') ?? element : element
      active.dataset.cbPlaceholder = row.block.type === 'td' ? 'Type in cell' : "Write, or type '/' for commands"
    }
    refresh()
    root.addEventListener('focusin', refresh)
    root.addEventListener('focusout', clear)
    return () => { clear(); root.removeEventListener('focusin', refresh); root.removeEventListener('focusout', clear) }
  }, [api])
  return null
}
